import { useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import {
  filter,
  finalize,
  fromEvent,
  map,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { Plane, Raycaster, Vector2, Vector3 } from 'three';

import { useSubscriptions } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import {
  useEditingLabelBoxStore,
  useLabelingBoxStore,
  useToolMainCameraStore,
  useToolMenuStore,
} from '@/store';
import {
  type NEW__LabelingBox,
  filterOrRestart,
  withCanvasXY,
  withTarget,
} from '@/util';

import { isEnabled, isTopMode, setControlEnabled } from '../util';

const EDGE_RAY_LINE_THRESHOLD = 0.3;
const LINE_HOVER_COLOR = 'yellow';
const VECTOR3_FOR_PLANE = new Vector3();

export const useDefaultViewTopResize = ({
  setRenderBoxListById,
  disabled,
}: {
  setRenderBoxListById: (boxIdList: string[]) => void;
  disabled: boolean;
}) => {
  const { getMode } = useToolMainCameraStore();
  const { camera, controls } = useThree();
  const isMoving = useEditingLabelBoxStore(state => state.isMoving);
  const isResizing = useEditingLabelBoxStore(state => state.isResizing);
  const isRotating = useEditingLabelBoxStore(state => state.isRotating);
  const { selectedLabelingBoxList } = useLabelingBoxContext();
  const selectedLabelingBoxListRef = useRef<NEW__LabelingBox[]>([]);
  const isMovingRef = useRef(false);
  const isResizingRef = useRef(false);
  const isRotatingRef = useRef(false);

  const cleanupResize = () => {
    useEditingLabelBoxStore.getState().clear();
    setControlEnabled(true, controls);
    setRenderBoxListById([]);
  };

  useSubscriptions(() => {
    const isAiAssistRunning = useToolMenuStore
      .getState()
      .isSelectedByToolName('aiAssist');

    if (isAiAssistRunning || !isEnabled(disabled) || !isTopMode(getMode())) {
      cleanupResize();

      return [];
    }

    return [
      fromEvent<MouseEvent>(window, 'mousemove')
        .pipe(
          withTarget,
          withCanvasXY,
          filter(() => selectedLabelingBoxListRef.current.length === 1),
          tap(e => {
            const raycaster = new Raycaster();
            raycaster.setFromCamera(new Vector2(e.canvasX, e.canvasY), camera);

            const edgeRay = new Raycaster();

            edgeRay.params.Line.threshold = EDGE_RAY_LINE_THRESHOLD;
            edgeRay.setFromCamera(new Vector2(e.canvasX, e.canvasY), camera);

            const selectedBox = selectedLabelingBoxListRef.current[0];
            const lineIntersect = edgeRay.intersectObject(selectedBox.line2);

            !isResizingRef.current && lineIntersect.at(0)
              ? selectedBox.setLineColorByIndex(
                  lineIntersect.at(0)!.faceIndex!,
                  LINE_HOVER_COLOR,
                )
              : selectedBox.color
                ? selectedBox.updateColor(selectedBox.color)
                : void 0;

            const grabIntersectionsList = [lineIntersect];
            const canvas = e.target?.closest('canvas') as HTMLCanvasElement;

            if (canvas) {
              if (isResizing || isMoving || isRotating) {
                canvas.style.setProperty('cursor', 'grabbing', 'important');
                canvas.setAttribute('data-cursor-priority', 'editing');
              } else if (
                grabIntersectionsList.some(
                  intersections => !!intersections.length,
                )
              ) {
                canvas.style.setProperty('cursor', 'grab', 'important');
                canvas.setAttribute('data-cursor-priority', 'editing');
              } else {
                canvas.removeAttribute('data-cursor-priority');
              }
            }
          }),
        )
        .subscribe(),

      fromEvent<PointerEvent>(window, 'pointerdown')
        .pipe(
          filter(e => e.buttons === 1),
          filter(() => selectedLabelingBoxListRef.current.length === 1),
          filter(() => !isMovingRef.current),
          filter(() => !isRotatingRef.current),
          withTarget,
          withCanvasXY,
          map(e => {
            const raycaster = new Raycaster();

            raycaster.params.Line.threshold = EDGE_RAY_LINE_THRESHOLD;
            raycaster.setFromCamera(new Vector2(e.canvasX, e.canvasY), camera);

            const selectedBox = selectedLabelingBoxListRef.current[0];

            const intersections = raycaster.intersectObject(
              selectedBox.line2,
              false,
            );

            return { event: e, raycaster, intersections, selectedBox };
          }),
          filter(({ intersections }) => intersections.length > 0),
          switchMap(({ event, intersections, selectedBox }) => {
            const [targetLine] = intersections;
            const startPoint = targetLine.point.clone();
            const planeNormal = camera
              .getWorldDirection(VECTOR3_FOR_PLANE)
              .clone()
              .negate();
            const plane = new Plane();
            const mouse = new Vector2(event.canvasX, event.canvasY);
            const raycaster = new Raycaster();

            plane.setFromNormalAndCoplanarPoint(planeNormal, startPoint);
            raycaster.setFromCamera(mouse, camera);

            const startMousePosition = new Vector3();
            const startPointWorld = targetLine.point.clone();
            const initSize = selectedBox.boxMesh.scale.clone();
            const initPosition = selectedBox.boxMesh.position.clone();
            const quaternion = selectedBox.boxMesh.quaternion;
            const inverseQuaternion = quaternion.clone().invert();

            raycaster.ray.intersectPlane(plane, startMousePosition);

            selectedBox.setLineColorByIndex(
              targetLine.faceIndex!,
              selectedBox.color!,
            );

            useEditingLabelBoxStore.getState().setIsResizing(true);
            useEditingLabelBoxStore.getState().setIsTransforming(true);

            if (controls) {
              controls.enablePan = false;
              controls.update();
            }

            return fromEvent<PointerEvent>(window, 'pointermove').pipe(
              withTarget,
              filterOrRestart(e => e.target instanceof HTMLCanvasElement),
              withCanvasXY,
              tap(event => {
                const mouse = new Vector2(event.canvasX, event.canvasY);
                const raycaster = new Raycaster();
                raycaster.setFromCamera(mouse, camera);

                const currentPoint = new Vector3();
                raycaster.ray.intersectPlane(plane, currentPoint);

                const worldDelta = currentPoint.clone().sub(startPointWorld);

                const edgeIndex = targetLine.faceIndex!;
                const isXEdge = edgeIndex === 12 || edgeIndex === 16;
                const isYEdge = edgeIndex === 14 || edgeIndex === 10;
                const box = selectedBox.boxMesh;
                const minSize = 0.1;

                const newSize = box.scale.clone();
                const newPosition = box.position.clone();
                const localDelta = worldDelta
                  .clone()
                  .applyQuaternion(inverseQuaternion);

                const localPositionOffset = new Vector3();

                if (isXEdge) {
                  const direction = edgeIndex === 16 ? -1 : 1;
                  const delta = localDelta.x * direction;
                  const newWidth = Math.max(minSize, initSize.x + delta);

                  newSize.x = newWidth;
                  localPositionOffset.x = localDelta.x / 2;
                }

                if (isYEdge) {
                  const direction = edgeIndex === 10 ? -1 : 1;
                  const delta = localDelta.y * direction;
                  const newHeight = Math.max(minSize, initSize.y + delta);

                  newSize.y = newHeight;
                  localPositionOffset.y = localDelta.y / 2;
                }

                const worldPositionOffset = localPositionOffset
                  .clone()
                  .applyQuaternion(quaternion);

                newPosition.copy(initPosition).add(worldPositionOffset);

                selectedBox.updatePosition(newPosition.toArray());
                selectedBox.updateDimension(newSize.toArray());
              }),
              takeUntil(
                fromEvent<PointerEvent>(window, 'pointerup').pipe(
                  tap(() => {
                    useLabelingBoxStore.temporal.getState().pause();
                    useLabelingBoxStore
                      .getState()
                      .moveBoxList([[selectedBox.id, selectedBox.position]]);
                    useLabelingBoxStore
                      .getState()
                      .resizeBoxList([[selectedBox.id, selectedBox.dimension]]);
                    useLabelingBoxStore.temporal.getState().resume();
                  }),
                  switchMap(() => timer(0)),
                  finalize(() => cleanupResize()),
                ),
              ),
            );
          }),
          switchMap(() => timer(0)),
          finalize(() => cleanupResize()),
        )
        .subscribe(),
    ];
  }, [camera, controls, disabled]);

  useEffect(() => {
    selectedLabelingBoxListRef.current = selectedLabelingBoxList;
  }, [selectedLabelingBoxList]);

  useEffect(() => {
    isMovingRef.current = isMoving;
    isResizingRef.current = isResizing;
    isRotatingRef.current = isRotating;
  }, [isMoving, isResizing, isRotating]);
};
